import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import './CallStatusDonut.scss';

interface AIvsHumanData {
    ai: number;
    human: number;
    totalCount: number;
}

interface AIvsHumanTimeBarGraphProps {
    data: AIvsHumanData;
    title?: string;
    height?: number;
}

interface ProcessedDataPoint {
    name: string;
    value: number;
    color: string;
}

/**
 * A bar graph displaying AI time vs Human time from call data
 */
function AIvsHumanTimeBarGraph({ data, title = 'AI vs Human Time' }: AIvsHumanTimeBarGraphProps): React.JSX.Element {
    const secondsToMinutes = (seconds: number): number => {
        return seconds / 60;
    };

    // Get full HH:MM:SS format
    const getFullTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours ? `${hours} hours ` : ''}${minutes ? `${minutes.toString().padStart(2, '0')} min ` : ''}${secs ? `${secs.toString().padStart(2, '0')} sec` : ''}`;
    };

    // Process the raw API data to calculate AI and Human time
    const processedData = useMemo((): ProcessedDataPoint[] => {
        return [
            {
                name: 'AI',
                value: secondsToMinutes(data.ai),
                color: '#9b51e0',
            },
            {
                name: 'Human',
                value: secondsToMinutes(data.human),
                color: '#4CAF50',
            },
        ];
    }, [data]);

    const tooltipFormater = (value: number, name: string): [string, string] => {
        const totalSeconds = value * 60;
        const duration = getFullTime(totalSeconds);
        return [duration, name];
    };

    return (
        <div className='metric-card'>
            <div className='metric-header'>
                <h3>{title}</h3>
                <span className='metric-value'>{`${getFullTime(data.totalCount)} total`}</span>
            </div>
            <div className='chart-wrapper'>
                <ResponsiveContainer width='100%' height='100%'>
                    <BarChart data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <XAxis
                            dataKey='name'
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fontWeight: 600 }}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10 }}
                            label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip
                            formatter={tooltipFormater}
                            labelFormatter={(label) => `${label} Time:`}
                            contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '8px',
                            }}
                            wrapperStyle={{ zIndex: 1000 }}
                        />
                        <Bar dataKey='value' radius={[4, 4, 0, 0]}>
                            {processedData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default AIvsHumanTimeBarGraph;
